import { isFixedCallPriceCriterion } from "../model";
import { useEffect,useState } from "react";
import { CheckBox,Modal,StaticDropdown,TextInput,Button } from "src/Components";
import { CoveredMatrixExecutionMethod,CoveredMatrixCallOption,CoveredMatrixGroup,CoveredMatrixMarketWatch } from "src/Interfaces/coveredMatrixOrder";
import { getCoveredMatrixLiveCriteria } from "src/Requests/coveredMatrixOrder";
import DeleteIcon from "@mui/icons-material/Delete";
import { executionMethodOptions,defaultCriterionType,criterionOptionsForExecutionMethod,tableRefreshInterval,isMaximumCostCriterion,criterionName,formatCriterionValue } from "../model";
import { Info } from "../Display";
import { CriterionValueInput } from "../CriterionValueInput";

export const EditCriteriaModal=({ group: initialGroup,options,marketWatch,accountName,baseName,optionName,onClose,onSubmit,loading }: {
  loading: boolean;
  group: CoveredMatrixGroup;
  options: CoveredMatrixCallOption[];
  marketWatch: Record<string,CoveredMatrixMarketWatch>;
  accountName: string;
  baseName: string;
  optionName: (value: string) => string;
  onClose: () => void;
  onSubmit: (group: CoveredMatrixGroup) => void;
}) => {
  const [group,setGroup]=useState<CoveredMatrixGroup>(() => ({
    ...initialGroup,
    items: initialGroup.items.map((item) => ({ ...item })),
    removedItems: [...(initialGroup.removedItems||[])],
  }));
  const [originalValues]=useState(() => new Map(group.items.map((item) => [item.id,item.criterionValue])));
  const [newOptionIsin,setNewOptionIsin]=useState("");
  const [liveCriteria,setLiveCriteria]=useState<Record<string,number|null>>({});
  const doneVolume=Math.max(group.sentVolume||0,...group.items.map((item) => item.doneVolume||0));
  const progress=group.targetVolume>0? Math.min(100,doneVolume*100/group.targetVolume):0;
  const executionMethodName=executionMethodOptions.find((item) => Number(item.id)===group.executionMethod)?.name||"-";
  const liveBasePrice=marketWatch[group.baseIsin]?.lastPrice;
  const referencePrice=group.manualBaseReferencePrice??liveBasePrice;
  const referencePriceLabel=group.executionMethod===CoveredMatrixExecutionMethod.FutureBasePurchase? "قیمت مبنای نماد پایه":"قیمت لایو سرخط روبرو";
  const remainingOptions=options.filter((option) => !group.items.some((item) => item.optionIsin===option.id.toString()));
  const selectedCriterionType=group.items[0]?.criterionType??defaultCriterionType;
  const criterionOptions=criterionOptionsForExecutionMethod(group.executionMethod);
  const liveCriteriaKey=JSON.stringify(group.items.map(({ optionIsin,criterionType }) => ({ optionIsin,criterionType })));
  useEffect(() => {
    const items: Array<{ optionIsin: string; criterionType: number }>=JSON.parse(liveCriteriaKey);
    if(!group.baseIsin||items.length===0) {
      setLiveCriteria({});
      return;
    }
    let disposed=false;
    const load=() => getCoveredMatrixLiveCriteria({
      baseIsin: group.baseIsin,
      executionMethod: group.executionMethod,
      manualBaseReferencePrice: group.manualBaseReferencePrice,
      items,
    })
      .then((items) => {
        if(!disposed)
          setLiveCriteria(Object.fromEntries(items.map((item) => [item.optionIsin,item.value])));
      })
      .catch(() => {
        if(!disposed)
          setLiveCriteria({});
      });
    load();
    const intervalId=window.setInterval(load,tableRefreshInterval);
    return () => {
      disposed=true;
      window.clearInterval(intervalId);
    };
  },[group.baseIsin,group.executionMethod,group.manualBaseReferencePrice,liveCriteriaKey]);
  useEffect(() => {
    if(!isMaximumCostCriterion(selectedCriterionType))
      return;
    setGroup((current) => {
      let changed=false;
      const items=current.items.map((item) => {
        const value=liveCriteria[item.optionIsin];
        if(item.criterionValue!==null||value===null||value===undefined||!Number.isFinite(Number(value)))
          return item;
        changed=true;
        return { ...item,criterionValue: Number(value) };
      });
      return changed? { ...current,items }:current;
    });
  },[liveCriteria,selectedCriterionType]);
  const updateCriterion=(id: number,value: number|null) => setGroup({
    ...group,
    items: group.items.map((item) => item.id===id? { ...item,criterionValue: value }:item),
  });
  const updateCriterionType=(_: string,value: string|number|null) => {
    const nextCriterionType=Number(value);
    setGroup({
      ...group,
      items: group.items.map((item) => ({ ...item,criterionType: nextCriterionType,criterionValue: null })),
    });
  };
  const removeItem=(id: number) => {
    const item=group.items.find((current) => current.id===id);
    if(!item)
      return;
    setGroup({
      ...group,
      items: group.items.filter((current) => current.id!==id),
      removedItems: item.id>0? [...(group.removedItems||[]),item]:group.removedItems,
    });
  };
  const addItem=() => {
    if(!newOptionIsin||group.items.some((item) => item.optionIsin===newOptionIsin))
      return;
    const selectedOption=options.find((option) => option.id.toString()===newOptionIsin);
    const restored=group.removedItems?.find((item) => item.optionIsin===newOptionIsin);
    setGroup({
      ...group,
      removedItems: group.removedItems?.filter((item) => item.optionIsin!==newOptionIsin),
      items: [...group.items,restored||{
        id: -Date.now(),
        optionIsin: newOptionIsin,
        optionName: selectedOption?.name||newOptionIsin,
        criterionType: selectedCriterionType,
        criterionValue: null,
        coveredCallOrderId: null,
        totalVolume: group.targetVolume,
        doneVolume: 0,
      }],
    });
    setNewOptionIsin("");
  };
  return <Modal
    onClose={onClose}
    modalBoxClassName="!w-[720px] !max-w-[95vw]"
    UI={<div className="p-4 flex flex-col gap-3" dir="rtl">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded bg-slate-50 p-3 text-xs">
        <Info label="اکانت" value={accountName} />
        <Info label="نماد پایه" value={baseName} />
        <Info
          label={referencePriceLabel}
          value={referencePrice==null? "در حال دریافت…":Number(referencePrice).toLocaleString()}
        />
        <Info label="روش اجرا" value={executionMethodName} />
        <div className="col-span-2 flex items-end gap-3">
          <div className="w-[150px]">
            <TextInput
              extraLabel
              label="کل حجم"
              kind="number"
              name="targetVolume"
              value={group.targetVolume.toString()}
              onChange={(_,value) => setGroup({ ...group,targetVolume: Number(value) })}
            />
          </div>
          <div className="flex-1 pb-1">
            <div className="mb-1 flex justify-between text-[10px] text-gray-500">
              <span>انجام‌شده: {doneVolume.toLocaleString()}</span>
              <span>{progress.toFixed(1)}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded bg-gray-200">
              <div className="h-full bg-sky-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>
      <div className="max-h-[300px] overflow-auto">
        <table className="mx-auto w-auto table-fixed text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="w-8 px-1 py-2">#</th>
              <th className="w-[150px] px-2 py-2">آپشن</th>
              <th className="w-[100px] px-2 py-2">مقدار لحظه‌ای</th>
              <th className="w-[145px] px-2 py-2">{criterionName(selectedCriterionType)}</th>
              <th className="w-[80px] px-2 py-2">حجم انجام</th>
              <th className="w-10 px-1 py-2">حذف</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((item,index) => {
              const originalValue=originalValues.get(item.id);
              const changed=item.id>0&&originalValue!==item.criterionValue;
              return <tr key={item.id} className="border-t">
                <td className="px-1 py-1 text-center text-gray-500">{index+1}</td>
                <td
                  className="max-w-[150px] truncate px-2 py-1"
                  title={item.optionName||optionName(item.optionIsin)}
                >
                  {item.optionName||optionName(item.optionIsin)}
                </td>
                <td className="px-2 py-1 text-center font-medium" dir="ltr">
                  {formatCriterionValue(liveCriteria[item.optionIsin],item.criterionType)}
                </td>
                <td className="px-2 py-1">
                  <div className="w-[125px]">
                    <CriterionValueInput
                      name={`criterion-${item.id}`}
                      value={item.criterionValue}
                      allowNegative={!isFixedCallPriceCriterion(item.criterionType)}
                      onChange={(value) => updateCriterion(item.id,value)}
                    />
                  </div>
                  {changed? <div className="mt-0.5 text-[9px] text-gray-400">مقدار قبلی:
                    {originalValue===null||originalValue===undefined? "خالی":formatCriterionValue(originalValue)}
                  </div>:null}
                </td>
                <td className="px-2 py-1 text-center">{(item.doneVolume||0).toLocaleString()}</td>
                <td className="px-1 py-1 text-center">
                  <button type="button" title="حذف آپشن" onClick={() => removeItem(item.id)} className="text-red-700">
                    <DeleteIcon fontSize="small" />
                  </button>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      <div className="w-[260px]">
        <StaticDropdown
          dropdown={{ name: "criterionType",placeholder: "نوع معیار",extraLabel: true }}
          options={criterionOptions}
          value={selectedCriterionType}
          onChange={updateCriterionType}
        />
      </div>
      <div className="grid grid-cols-[1fr_80px] items-end gap-2">
        <StaticDropdown
          dropdown={{ name: "newOption",placeholder: "افزودن نماد سمت دوم",extraLabel: true }}
          options={remainingOptions}
          value={newOptionIsin}
          onChange={(_,value) => setNewOptionIsin(value?.toString()||"")}
        />
        <Button
          variant="outlined"
          text="افزودن"
          onClick={addItem}
          disabled={!newOptionIsin}
          className="!h-9"
        />
      </div>
      <CheckBox
        name="applyToSimilar"
        label="ویرایش برای همه سفارش‌های مشابه"
        checked={Boolean(group.applyToSimilar)}
        onChange={(_,value) => setGroup({ ...group,applyToSimilar: value })}
      />
      <div className="flex justify-between pt-3">
        <Button variant="outlined" text="لغو" onClick={onClose} className="!w-20" />
        <Button
          variant="contained"
          text="ویرایش"
          loading={loading}
          disabled={loading}
          onClick={() => onSubmit(group)}
          className="!w-20"
        />
      </div>
    </div>}
  />;
};
